import { useEffect, useState } from "react";

import { Button, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useQuery } from "@tanstack/react-query";

import AddressForm from "./common/AddressForm";
import DwellingAdditionalForm from "./DwellingAdditionalForm";
import DwellingAddressForm from "./DwellingAddressForm";
import DwellingAssociateBuildingForm from "./DwellingAssociateBuildingForm";
import DwellingEquipmentForm from "./DwellingEquipmentForm";
import DwellingFeatureForm from "./DwellingFeatureForm";
import { ErrorOrigin, Errors } from "../../../_helpers/errorType";
import { listAgency } from "../../../_services/serviceService.ts";
import Loader from "../../../common/components/Loader";
import SelectAgency from "../../../common/components/SelectAgency.tsx";
import UploadDocumentForm from "../../../common/components/UploadDocumentForm";
import { FormType, Permissions } from "../../../common/enums/sharedEnums";
import { Agency } from "../../../common/interfaces.ts";
import history from "../../../routes/history";
import { IAddress } from "../../building/interfaces";
import { formTypeText } from "../constants/index.ts";
import { PropsFromReduxAppDwellingForm } from "../containers/DwellingFormContainer";
import { DwellingEquipmentType } from "../enums";
import { IDwellingAddress, IDwellingEquipment } from "../interfaces";
import { IDwelling, initialDwelling } from "../reducers";
import { DWELLING_DETAILS, DWELLINGS } from "../routes";
import { getDwellingInfos } from "../services";

interface RouteParams {
  id: string;
  isAssociatingBuilding?: string;
}

// @todo convert these form into react-hook-form form
const DwellingForm = (props: PropsFromReduxAppDwellingForm) => {
  const { id, isAssociatingBuilding } = useParams<RouteParams>();
  const { data } = useQuery<IDwelling, Error>(
    ["dwelling", id],
    () => getDwellingInfos(id),
    {
      enabled: !!id,
      staleTime: Infinity,
    }
  );

  const [address, setAddress] = useState<IAddress>({
    additionalAddress: "",
    city: "",
    address: "",
    latitudeY: "",
    longitudeX: "",
    zip: "",
    digicode: "",
    id: "",
    residenceName: "",
  });
  const [airConditioner, setAirConditioner] = useState<IDwellingEquipment>({
    energies: [],
    id: "",
    location: "",
    number: "",
    isCollective: false,
    type: DwellingEquipmentType.AIR_CONDITIONER,
  });
  const [coldWater, setColdWater] = useState<IDwellingEquipment>({
    isCollective: true,
    location: "",
    number: "",
    type: DwellingEquipmentType.COLD_WATER,
  });
  const [dwellingAddress, setDwellingAddress] = useState<IDwellingAddress>({
    cellarNumber: "",
    door: "",
    floor: "",
    garageBoxNumber: "",
    indoorParkingNumber: "",
    outdoorParkingNumber: "",
    stair: "",
    buildingNumber: "",
    cadastralReference: "",
    entrance: "",
    address: address,
  });
  const [dwellingDocuments, setDwellingDocuments] = useState<Array<any>>([]);
  const [dwelling, setDwelling] = useState<IDwelling>(initialDwelling);
  const [agency, setAgency] = useState<{ label: string; value: string }>();
  const [errors, setErrors] = useState<Errors>({
    [ErrorOrigin.INPUT]: [],
    [ErrorOrigin.API]: [],
  });
  const [formType, setFormType] = useState<FormType>(FormType.CREATE);
  const [heater, setHeater] = useState<IDwellingEquipment>({
    energies: [],
    isCollective: false,
    id: "",
    location: "",
    number: "",
    type: DwellingEquipmentType.HEATER,
  });
  const [hotWater, setHotWater] = useState<IDwellingEquipment>({
    energies: [],
    isCollective: false,
    location: "",
    number: "",
    type: DwellingEquipmentType.HOT_WATER,
  });
  const [installedAC, setInstalledAC] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  //AssociateBuildingForm
  const [activePage, setActivePage] = useState(1);
  const [building, setBuilding] = useState<any>();
  const [inputFilter, setInputFilter] = useState("");

  useEffect(() => {
    props.getBuildings(activePage, inputFilter);
  }, [inputFilter]); //eslint-disable-line

  useEffect(() => {
    if (data) {
      setLoading(true);
      props
        .getDwelling(id)
        .then(() => {
          if (data.buildingId) {
            props.getBuilding(data.buildingId);
            setBuilding(props.building);
          }
          setFormType(FormType.UPDATE);
          setDwellingDocuments(data.files || []);
          data.equipments.forEach(setEquipmentState);
          setAddress({
            additionalAddress: data.address.address.additionalAddress,
            city: data.address.address.city,
            address: data.address.address.address,
            latitudeY: data.address.address.latitudeY,
            longitudeX: data.address.address.longitudeX,
            zip: data.address.address.zip,
            digicode: data.address.address.digicode,
            id: data.address.address.id,
            residenceName: data.address.address.residenceName,
          });
          setDwellingAddress({
            cellarNumber: data.address.cellarNumber,
            door: data.address.door,
            floor: data.address.floor,
            garageBoxNumber: data.address.garageBoxNumber,
            id: data.address.id,
            indoorParkingNumber: data.address.indoorParkingNumber,
            outdoorParkingNumber: data.address.outdoorParkingNumber,
            stair: data.address.stair,
            address: address,
          });
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          setDwelling({
            id: id,
            agency: data.agency,
            ademeNumber: data.ademeNumber,
            address: dwellingAddress,
            addressLabel: data.addressLabel,
            area: data.area,
            building: building,
            category: data.category,
            comment: data.comment,
            condition: data.condition,
            constructionDate: data.constructionDate,
            constructionPeriod: data.constructionPeriod,
            files: dwellingDocuments,
            equipments: data.equipments,
            isCondominium: data.isCondominium,
            isFurnished: data.isFurnished,
            isSociety: data.isSociety,
            lessorFirstname: data.lessorFirstname,
            lessorName: data.lessor ? data.lessor.name : data.lessorName,
            managementLotNumber: data.managementLotNumber,
            taxNumber: data.taxNumber,
            linkVirtualVisit: data.linkVirtualVisit,
            type: data.type,
            key: data.key,
          });
          setLoading(false);
        })
        .catch(() => {
          toast.error("Aucun bien correspondant");
          history.push(DWELLINGS);
        });
    }
  }, [data]); //eslint-disable-line

  useEffect(() => {
    if (undefined !== building) {
      setAddress({
        additionalAddress:
          address.additionalAddress || building.address.additionalAddress,
        address: address.address || building.address.address,
        city: address.city || building.address.city,
        digicode: address.digicode || building.address.digicode,
        id: address.id || building.address.id || undefined,
        residenceName: address.residenceName || building.address.residenceName,
        zip: address.zip || building.address.zip,
      });
      setDwelling({
        ...dwelling,
        buildingId: building.id,
        constructionPeriod: building.constructionPeriod,
      });
      setHeater({ ...heater, isCollective: building.isCollective });
    } else {
      setBuilding(undefined);
      setDwelling({
        ...dwelling,
        buildingId: undefined,
      });
    }
  }, [building]); //eslint-disable-line

  const checkValues = async () => {
    const inputErrors: Error[] = [];

    if (!agency) {
      inputErrors.push({
        rule: "required",
        field: "agency",
        message: "Veuillez renseigner une agence",
      });
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [ErrorOrigin.INPUT]: inputErrors,
    }));
    return inputErrors;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrors({ [ErrorOrigin.INPUT]: [], [ErrorOrigin.API]: [] });

    const inputErrors = await checkValues();

    if (inputErrors.length > 0) {
      setLoading(false);
      toast.warning(formTypeText[formType].error);
      for (const error of inputErrors) {
        toast.error(error.message);
      }
    } else {
      if (formType === FormType.UPDATE && id && agency) {
        props
          .updateDwelling({
            ...dwelling,
            condition: dwelling.condition,
            lessorName: dwelling.lessorId
              ? dwelling.lessorId.name
              : dwelling.lessorName,
            lessorId: dwelling.lessorId.value,
            agencyId: agency.value,
            address: {
              ...dwellingAddress,
              address: address,
            },
            building: building,
            equipments: [airConditioner, coldWater, heater, hotWater],
          })
          .then(() => {
            if (dwellingDocuments.length > 0) {
              props.uploadDwellingFiles(id as string, dwellingDocuments);
            }
            setLoading(false);
            toast.success(formTypeText[formType].success);
            history.push(
              DWELLING_DETAILS.replace(":id", dwelling.datedReference || id)
            );
          })
          .catch((e) => {
            setLoading(false);
            setErrors({ ...errors, [ErrorOrigin.API]: e ?? [] });
            toast.error(formTypeText[formType].error);
          });
      } else {
        if (agency)
          props
            .addDwelling({
              ...dwelling,
              lessorName: dwelling.lessorId
                ? dwelling.lessorId.name
                : dwelling.lessorName,
              lessorId: dwelling.lessorId.value,
              address: {
                ...dwellingAddress,
                address: address,
              },
              building: building,
              equipments: [airConditioner, coldWater, heater, hotWater],
              agencyId: agency?.value,
            })
            .then((id) => {
              if (dwellingDocuments.length > 0) {
                props.uploadDwellingFiles(id as string, dwellingDocuments);
              }
              setLoading(false);
              toast.success(formTypeText[formType].success);
              history.push(DWELLINGS);
            })
            .catch((e) => {
              console.log(e);
              setLoading(false);
              setErrors({ ...errors, [ErrorOrigin.API]: e ?? [] });
              toast.error(formTypeText[formType].error);
            });
      }
    }
  };

  const setEquipmentState = (equipment: IDwellingEquipment) => {
    const { energies, id, isCollective, location, number, type } = equipment;
    switch (type) {
      case DwellingEquipmentType.AIR_CONDITIONER:
        setInstalledAC(true);
        setAirConditioner({
          energies,
          id,
          location,
          number,
          isCollective,
          type,
        });
        break;
      case DwellingEquipmentType.COLD_WATER:
        setColdWater({ energies, id, location, number, isCollective, type });
        break;
      case DwellingEquipmentType.HEATER:
        setHeater({ energies, id, location, number, isCollective, type });
        break;
      case DwellingEquipmentType.HOT_WATER:
        setHotWater({ energies, id, location, number, isCollective, type });
        break;
      default:
        break;
    }
  };

  const removeFile = (id: string) => {
    props
      .removeDwellingFile(dwelling.id, id)
      .then(() => {
        toast.success("Le fichier à été supprimé");
      })
      .catch(() => {
        toast.error("Impossible de supprimé le fichier");
      });
  };

  const {
    data: agencies,
    isLoading: loadingAgencies,
    error: errorAgencies,
  } = useQuery<Agency[], Error>(
    ["listAgency", Permissions.BUILDING_DWELLING_CREATE],
    () => listAgency(Permissions.BUILDING_DWELLING_CREATE)
  );

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="mb-3  mt-4 text-center text-lg-start d-lg-block">
              {formTypeText[formType].title}
            </h1>
            <div className="bg-container bg-container-form mb-3">
              <div className="d-flex align-items-center gap-3">
                <label className="add-prop-label m-0">Agence :</label>
                <div className="select-creatable-team-member-form">
                  <SelectAgency
                    loading={loadingAgencies}
                    error={errorAgencies}
                    agencies={agencies}
                    rightValue={Permissions.BUILDING_DWELLING_CREATE}
                    defaultValue={data?.agency?.id}
                    setAgency={(value) => setAgency(value)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-container bg-container-form">
            <div className="d-flex d-flex-row justify-content-between">
              <h2
                className="fw-bold main-title mb-3"
                style={{ fontSize: "24px" }}
              >
                Caractéristiques du bien
              </h2>
            </div>
            <div className="bg-container bg-container-form">
              <DwellingFeatureForm
                dwelling={dwelling}
                setDwelling={setDwelling}
                referentials={props.referentials}
              />
            </div>
            <h2
              className="fw-bold main-title my-4"
              style={{ fontSize: "24px" }}
            >
              Informations sur le bien
            </h2>
            <div className="bg-container bg-container-form">
              <div className="d-flex flex-column gap-3">
                <AddressForm
                  defaultAddress={dwelling?.addressLabel}
                  address={address}
                  dwellingAddress={dwellingAddress}
                  setAddress={setAddress}
                  setDwellingAddress={setDwellingAddress}
                />
                <DwellingAddressForm
                  dwellingAddress={dwellingAddress}
                  setDwellingAddress={setDwellingAddress}
                />
                <DwellingAssociateBuildingForm
                  activePage={activePage}
                  building={building}
                  buildings={props.buildings}
                  isDisplayedByDefault={isAssociatingBuilding === "true"}
                  inputFilter={inputFilter}
                  setActivePage={setActivePage}
                  setBuilding={setBuilding}
                  setInputFilter={setInputFilter}
                />
              </div>
            </div>
            <h2
              className="fw-bold main-title  my-4"
              style={{ fontSize: "24px" }}
            >
              Équipements
            </h2>
            <div className="bg-container bg-container-form d-flex flex-column gap-4">
              <div className="d-flex flex-wrap gap-lg-5">
                <DwellingEquipmentForm
                  equipment={coldWater}
                  setEquipment={setColdWater}
                  referentials={props.referentials}
                />
              </div>
              <div className="border-bottom" />
              <div className="d-flex flex-wrap gap-lg-5">
                <DwellingEquipmentForm
                  equipment={hotWater}
                  setEquipment={setHotWater}
                  referentials={props.referentials}
                />
              </div>
              <div className="border-bottom" />
              <div className="d-flex flex-wrap gap-lg-5">
                <DwellingEquipmentForm
                  equipment={heater}
                  setEquipment={setHeater}
                  referentials={props.referentials}
                />
              </div>
              <div className="border-bottom" />
              <div className="d-flex flex-wrap gap-lg-5">
                <DwellingEquipmentForm
                  equipment={airConditioner}
                  setEquipment={setAirConditioner}
                  installedAC={installedAC}
                  setInstalledAC={setInstalledAC}
                  referentials={props.referentials}
                />
              </div>
            </div>
            <h2
              className="fw-bold main-title my-4"
              style={{ fontSize: "24px" }}
            >
              Informations complémentaires
            </h2>
            <div className="mt-4 bg-container bg-container-form">
              <DwellingAdditionalForm
                dwelling={dwelling}
                setDwelling={setDwelling}
                referentials={props.referentials}
              />
            </div>

            <h2
              className="fw-bold main-title my-4"
              style={{ fontSize: "24px" }}
            >
              Importer des documents
            </h2>
            <div className="mt-4 bg-container bg-container-form">
              <Row>
                <UploadDocumentForm
                  documents={dwellingDocuments}
                  setDocuments={setDwellingDocuments}
                  referentials={props.referentials}
                  removeFile={removeFile}
                />
              </Row>
            </div>
            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="success"
                style={{ padding: "15px 25px" }}
                onClick={handleSubmit}
              >
                Enregistrer
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default DwellingForm;
